/**
 * Client-side projection of the RFC 9457 problem+json error shape plus the
 * transport-level failure modes (network, parse, aborted). Every API call
 * from the UI converts non-2xx responses into `ApiError`.
 */
export interface ProblemBody {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Array<{ path: string; message: string }>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail?: string;
  readonly requestId?: string;
  readonly fieldErrors?: Array<{ path: string; message: string }>;
  readonly retryAfterSeconds?: number;
  readonly body?: ProblemBody;

  constructor(init: {
    status: number;
    title: string;
    detail?: string;
    requestId?: string;
    fieldErrors?: Array<{ path: string; message: string }>;
    retryAfterSeconds?: number;
    body?: ProblemBody;
  }) {
    super(init.detail ?? init.title);
    this.name = 'ApiError';
    this.status = init.status;
    this.title = init.title;
    this.detail = init.detail;
    this.requestId = init.requestId;
    this.fieldErrors = init.fieldErrors;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.body = init.body;
  }

  /** True when the server told us the record changed under our feet (ETag). */
  get isConflict(): boolean {
    return this.status === 412 || this.status === 409;
  }

  /** True when the session is no longer valid. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True when the caller lacks permission for the operation. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True when the UI should retry later. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export class ApiNetworkError extends ApiError {
  constructor(message: string) {
    super({ status: 0, title: 'Network Error', detail: message });
    this.name = 'ApiNetworkError';
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * The server's own explanation, for a toast that has to tell the operator what
 * went wrong — a delete refused with 409 already names the schedules, masters,
 * rules or designation blocking it, and re-deriving that client-side would be a
 * second copy that can drift. Falls back to the title, then to the raw value.
 */
export function problemDetail(err: unknown): string {
  if (isApiError(err)) return err.detail ?? err.title;
  if (err && typeof err === 'object' && 'data' in err) {
    return (err as { data?: { detail?: string } }).data?.detail ?? String(err);
  }
  return String(err);
}
